import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';

// Icons / visual accent for each method, keyed by MethodCode
const METHOD_ACCENTS = {
  DIRECT_UPLOAD: { icon: '📤', color: '#1a237e' },
  MANUAL_GENERATION: { icon: '✏', color: '#2e7d32' },
  AUTO_GENERATION: { icon: '⚡', color: '#e65100' },
};

function QPMethodsTab({ tenantId }) {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const loadMethods = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const res = await api.get(`/qp-settings/qp-methods/${tenantId}`);
      if (res.data.success) {
        setMethods(res.data.data || []);
      }
    } catch {
      toast.error('Failed to load QP methods');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadMethods();
  }, [loadMethods]);

  const handleToggle = async (method) => {
    const next = !method.IsEnabled;

    // Prevent turning off the last remaining enabled method
    const enabledCount = methods.filter((m) => m.IsEnabled).length;
    if (!next && enabledCount <= 1) {
      return toast.error('At least one QP method must remain enabled.');
    }

    setSavingId(method.SettingID);
    // Optimistic update
    setMethods((prev) =>
      prev.map((m) => (m.SettingID === method.SettingID ? { ...m, IsEnabled: next } : m))
    );

    try {
      await api.put(`/qp-settings/qp-methods/${method.SettingID}`, { isEnabled: next });
      toast.success(`${method.MethodName} ${next ? 'enabled' : 'disabled'}`);
    } catch (err) {
      // Revert on failure
      setMethods((prev) =>
        prev.map((m) => (m.SettingID === method.SettingID ? { ...m, IsEnabled: !next } : m))
      );
      toast.error(err.response?.data?.message || 'Failed to update method');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <div
        className="page-card-header"
        style={{ margin: 0, paddingBottom: 16, borderBottom: '1px solid #e0e0e0' }}
      >
        <h3>QP Methods</h3>
        <span style={{ fontSize: 13, color: '#616161' }}>
          Choose which question paper generation methods are available for your institute.
        </span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : methods.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No QP methods configured.
        </div>
      ) : (
        <div className="qp-methods-grid">
          {methods.map((m) => {
            const accent = METHOD_ACCENTS[m.MethodCode] || { icon: '●', color: '#1a237e' };
            const isSaving = savingId === m.SettingID;
            return (
              <div
                key={m.SettingID}
                className={`qp-method-card ${m.IsEnabled ? 'enabled' : 'disabled'}`}
              >
                <div className="qp-method-card-header">
                  <div
                    className="qp-method-icon"
                    style={{ background: `${accent.color}15`, color: accent.color }}
                  >
                    {accent.icon}
                  </div>
                  <div className="qp-method-title">
                    <div className="qp-method-name">{m.MethodName}</div>
                    <div className="qp-method-code">{m.MethodCode}</div>
                  </div>
                  <label className="toggle-switch" title={m.IsEnabled ? 'Enabled' : 'Disabled'}>
                    <input
                      type="checkbox"
                      checked={!!m.IsEnabled}
                      disabled={isSaving}
                      onChange={() => handleToggle(m)}
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>

                <div className="qp-method-description">{m.Description}</div>

                <div className="qp-method-footer">
                  <span className={`badge ${m.IsEnabled ? 'badge-active' : 'badge-inactive'}`}>
                    {m.IsEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                  {isSaving && (
                    <span style={{ fontSize: 12, color: '#616161' }}>Saving...</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export default QPMethodsTab;
